/** The Customize session's behaviour, once, for both Configure bars (SA-416):
 * debounced server validation, save with its basis, the write conflict, and
 * Reload — which, since SA-1410, keeps the session's edits over a newly live
 * version and lists the settings where the published value won. The bars are
 * markup over this. */
import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  fetchWorkflowConfig,
  useSaveWorkflowConfig,
  useValidateWorkflowConfig,
  workflowConfigQueryKey,
  type PublishBasis,
} from '@/hooks/useWorkflowConfig'
import { rebaseOverlay } from '@/utils/overlayRebase'
import { writeConflict, type WriteConflict } from '@/utils/writeConflict'
import type {
  WorkflowConfigRead,
  WorkflowConfigValidateResponse,
  WorkflowOverlayJson,
} from '@/types/workflowConfig'

/** What the hook needs of an edit-session store — `workflowConfigStore`
 * (order rows) and `processConfigStore` (process rows) both satisfy it. */
export interface ConfigureSessionStore<B> {
  overlay: WorkflowOverlayJson
  /** The overlay the session's edits are measured against. */
  savedDraft: WorkflowOverlayJson | null
  validation: WorkflowConfigValidateResponse | null
  expectedUpdatedAt: string | null
  baseVersionNumber: number | null
  versionNumber: number | null
  isDirty(): boolean
  overlaySnapshot(): WorkflowOverlayJson
  markSaved(draft: WorkflowOverlayJson, updatedAt: string | null | undefined, workflowId: string): void
  setValidation(v: WorkflowConfigValidateResponse | null): void
  beginEditing(
    workflowId: string,
    base: B,
    savedDraft: WorkflowOverlayJson | null,
    expectedUpdatedAt?: string | null,
    baseVersionNumber?: number | null,
    versionNumber?: number | null,
  ): void
  rebaseOnto(
    base: B,
    start: WorkflowOverlayJson,
    overlay: WorkflowOverlayJson,
    expectedUpdatedAt: string | null,
    baseVersionNumber: number | null,
    versionNumber: number | null,
  ): void
}

export function useConfigureSession<B>(workflowId: string, store: ConfigureSessionStore<B>) {
  const save = useSaveWorkflowConfig(workflowId)
  const validate = useValidateWorkflowConfig(workflowId)
  const queryClient = useQueryClient()
  const [reloading, setReloading] = useState(false)
  const [validateFailed, setValidateFailed] = useState(false)
  const [conflict, setConflict] = useState<WriteConflict | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [reloadCount, setReloadCount] = useState(0)
  /** SA-1410: the settings a keep-edits Reload let the published value win. */
  const [rebaseConflicts, setRebaseConflicts] = useState<string[]>([])

  const overlayJson = JSON.stringify(store.overlay)
  const { baseVersionNumber, expectedUpdatedAt, versionNumber } = store
  // One answer is for ONE (overlay, base, reload). After a base-moved Reload
  // the overlay is byte-identical BY DESIGN (the edits are kept), so the text
  // alone would never re-ask — and the base NUMBER does not always move
  // either: a `draft_base_moved` from the publish claim (someone else's
  // publish or a restore landed) reloads onto the same number while the "vs
  // live" half of the diff is now wrong. `reloadCount` covers that.
  const issueKey = `${overlayJson}|${baseVersionNumber}|${reloadCount}`
  const issueKeyRef = useRef(issueKey)
  issueKeyRef.current = issueKey

  // Debounced server validation — semantic truth stays server-side (§5.3).
  useEffect(() => {
    store.setValidation(null) // a stale answer must never gate Publish for different content
    setValidateFailed(false)
    const issuedFor = issueKey
    const body = JSON.parse(overlayJson) as WorkflowOverlayJson
    const timer = setTimeout(() => {
      validate.mutate(body, {
        onSuccess: (result) => {
          // Ignore answers for content, a base or a reload that is no longer current.
          if (issueKeyRef.current === issuedFor) store.setValidation(result)
        },
        onError: () => {
          if (issueKeyRef.current === issuedFor) setValidateFailed(true)
        },
      })
    }, 600)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlayJson, workflowId, baseVersionNumber, reloadCount])

  const basis: PublishBasis = { expectedUpdatedAt, baseVersionNumber, versionNumber }

  function handleSave() {
    setSaveError(null)
    // The session this save belongs to, captured now: an answer that lands
    // after Exit or a move to another workflow must not touch the next one.
    const startedIn = workflowId
    // A save asserts the live version too (SA-1410): when it moved, the
    // person is told now, with their edits still unsaved, and Reload keeps
    // them. A successful save leaves `versionNumber` alone — the live version
    // did not move.
    save.mutate(
      { draft: store.overlaySnapshot(), expectedUpdatedAt, baseVersionNumber, versionNumber },
      {
        onSuccess: (data) => {
          store.markSaved(data.draft ?? {}, data.draft_updated_at ?? null, startedIn)
          setRebaseConflicts([])
        },
        onError: (error) => {
          const refused = writeConflict(error)
          if (refused) setConflict(refused)
          else setSaveError("Couldn't save the draft — try again.")
        },
      },
    )
  }

  /** Base or live version moved and nobody else saved: keep the author's
   * edits, rebased leaf by leaf onto what is stored or live now (the
   * published value wins where both changed a setting). Anything else: begin
   * again from what is stored. */
  async function handleReload() {
    setReloading(true)
    let data: WorkflowConfigRead
    try {
      // `fetchQuery` REJECTS on failure (a mounted query's `refetch()` resolves
      // with the cached data — the trap SA-404 paid for) and refreshes the
      // cache the page reads. The bar mounts no query of its own.
      data = await queryClient.fetchQuery({
        queryKey: workflowConfigQueryKey(workflowId),
        queryFn: () => fetchWorkflowConfig(workflowId),
        staleTime: 0,
        retry: false, // a failed Reload says so at once
      })
    } catch {
      setReloading(false)
      return setSaveError("Couldn't reload this configuration — try again.")
    }
    setReloading(false)
    const base = data.base_definition as B
    if (
      (conflict === 'base_moved' || conflict === 'published_since') &&
      data.draft_updated_at === expectedUpdatedAt
    ) {
      const fresh = data.draft ?? data.live_overlay ?? {}
      const { overlay, conflicts } = rebaseOverlay(store.overlay, store.savedDraft ?? {}, fresh)
      store.rebaseOnto(
        base,
        fresh,
        overlay,
        data.draft_updated_at,
        data.base_version_number,
        data.current_version_number ?? null,
      )
      setRebaseConflicts(conflicts)
    } else {
      store.beginEditing(
        workflowId,
        base,
        data.draft ?? data.live_overlay ?? null,
        data.draft_updated_at,
        data.base_version_number,
        data.current_version_number ?? null,
      )
      setRebaseConflicts([])
    }
    setConflict(null)
    setSaveError(null)
    setReloadCount((n) => n + 1)
  }

  const dirty = store.isDirty()
  const overlayEmpty = Object.keys(store.overlay).length === 0
  return {
    dirty,
    overlayEmpty,
    canPublish:
      !dirty && !overlayEmpty && store.validation?.valid === true && !save.isPending && conflict === null,
    saving: save.isPending,
    reloading,
    validateFailed,
    conflict,
    saveError,
    basis,
    rebaseConflicts,
    handleSave,
    handleReload,
    raiseConflict: setConflict,
  }
}
